import { cjk } from '@streamdown/cjk'
import { code } from '@streamdown/code'
import { Code, FileText, Pencil, Save, Type, X } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import { Streamdown } from 'streamdown'
import { getDownloadUrl, updateTextContent } from '@/client/sdk.gen'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactMetadataBlock } from '@/components/project/artifact-viewer/ArtifactMetadataBlock'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { extractErrorMessage } from '@/utils/error'

function isMarkdown(artifact: ArtifactResponse): boolean {
	if (artifact.mime_type === 'text/markdown' || artifact.mime_type === 'text/x-markdown') {
		return true
	}
	const ext = artifact.extension.replace(/^\./, '').toLowerCase()
	return ext === 'md' || ext === 'markdown'
}

export interface TextViewerProps {
	artifact: ArtifactResponse
	activeTab: 'preview' | 'metadata'
	onUnsavedChange?: (hasUnsaved: boolean) => void
}

export function TextViewer({ artifact, activeTab, onUnsavedChange }: TextViewerProps) {
	const { t } = useTranslation()
	const [content, setContent] = React.useState<string | null>(null)
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)
	const [editing, setEditing] = React.useState(false)
	const [editContent, setEditContent] = React.useState('')
	const [saving, setSaving] = React.useState(false)
	const [renderMarkdown, setRenderMarkdown] = React.useState<boolean | null>(null)

	const markdownFile = isMarkdown(artifact)
	const markdown = renderMarkdown ?? markdownFile

	React.useEffect(() => {
		setContent(null)
		setError(null)
		setLoading(false)
		setEditing(false)

		if (artifact.status !== 'completed') return

		let cancelled = false

		const fetchContent = async () => {
			setLoading(true)
			try {
				const response = await getDownloadUrl({
					path: { project_id: artifact.project_id, artifact_id: artifact.id },
				})

				if (cancelled) return

				if (response.error || !response.data?.download_url) {
					setError(
						extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorLoading'))
					)
					return
				}

				const textResponse = await fetch(response.data.download_url)
				if (cancelled) return

				if (!textResponse.ok) {
					setError(t('pages.projectDetail.artifactPreview.errorLoading'))
					return
				}

				const text = await textResponse.text()
				if (cancelled) return

				setContent(text)
			} catch (err) {
				if (cancelled) return
				setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchContent().catch((err) => {
			if (cancelled) return
			setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
			setLoading(false)
		})

		return () => {
			cancelled = true
		}
	}, [artifact.id, artifact.project_id, artifact.status, t])

	const handleEdit = () => {
		if (content === null) return
		setEditContent(content)
		setEditing(true)
	}

	const handleCancelEdit = () => {
		setEditing(false)
		setEditContent('')
	}

	const handleSave = async () => {
		setSaving(true)
		try {
			const response = await updateTextContent({
				path: { project_id: artifact.project_id, artifact_id: artifact.id },
				body: { content: editContent },
			})

			if (response.error) {
				throw new Error(
					extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorSaving'))
				)
			}

			setContent(editContent)
			setEditing(false)
			toast.success(t('pages.projectDetail.artifactPreview.saved'))
		} catch (err) {
			toast.error(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorSaving')))
		} finally {
			setSaving(false)
		}
	}

	const canEdit = activeTab === 'preview' && content !== null && artifact.status === 'completed'
	const hasUnsavedChanges = editing && editContent !== content

	React.useEffect(() => {
		onUnsavedChange?.(hasUnsavedChanges)
	}, [hasUnsavedChanges, onUnsavedChange])

	React.useEffect(() => {
		return () => onUnsavedChange?.(false)
	}, [onUnsavedChange])

	return (
		<div className="flex h-full min-h-0 flex-col">
			{activeTab === 'preview' && (
				<div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4">
					{canEdit && (
						<div className="flex items-center justify-end gap-2 pb-3">
							{!editing && (
								<button
									type="button"
									onClick={() => setRenderMarkdown((prev) => !(prev ?? markdownFile))}
									className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
									aria-label={
										markdown
											? t('pages.projectDetail.artifactPreview.viewRaw')
											: t('pages.projectDetail.artifactPreview.viewMarkdown')
									}
									title={
										markdown
											? t('pages.projectDetail.artifactPreview.viewRaw')
											: t('pages.projectDetail.artifactPreview.viewMarkdown')
									}
								>
									{markdown ? (
										<Code className="size-4" aria-hidden />
									) : (
										<Type className="size-4" aria-hidden />
									)}
								</button>
							)}
							{editing ? (
								<>
									<button
										type="button"
										onClick={handleCancelEdit}
										disabled={saving}
										className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
										aria-label={t('pages.projectDetail.artifactPreview.cancelEdit')}
									>
										<X className="size-4" aria-hidden />
										{t('pages.projectDetail.artifactPreview.cancelEdit')}
									</button>
									<button
										type="button"
										onClick={handleSave}
										disabled={saving || editContent === content}
										className="flex items-center gap-1.5 rounded-lg bg-[#8c52ff] px-3 py-1.5 text-sm text-white hover:bg-[#7b45e6] transition-colors disabled:opacity-50"
										aria-label={t('pages.projectDetail.artifactPreview.save')}
									>
										{saving ? (
											<Spinner className="size-4" aria-hidden />
										) : (
											<Save className="size-4" aria-hidden />
										)}
										{saving
											? t('pages.projectDetail.artifactPreview.saving')
											: t('pages.projectDetail.artifactPreview.save')}
									</button>
								</>
							) : (
								<button
									type="button"
									onClick={handleEdit}
									className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
									aria-label={t('pages.projectDetail.artifactPreview.edit')}
								>
									<Pencil className="size-4" aria-hidden />
									{t('pages.projectDetail.artifactPreview.edit')}
								</button>
							)}
						</div>
					)}
					{editing ? (
						<textarea
							className="min-h-0 flex-1 w-full resize-none rounded-lg border border-border bg-background p-4 text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-[#8c52ff]/50"
							value={editContent}
							onChange={(e) => setEditContent(e.target.value)}
							spellCheck={false}
						/>
					) : (
						<div className="relative min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-muted/20">
							{artifact.status !== 'completed' ? (
								<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
									<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
									<p className="mt-3 text-sm font-medium text-foreground">
										{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{t('pages.projectDetail.artifactPreview.previewPending')}
									</p>
								</div>
							) : loading ? (
								<div className="flex min-h-full items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
									<Spinner className="size-4" aria-hidden />
									{t('common.loading')}
								</div>
							) : error ? (
								<div className="flex min-h-full items-center justify-center p-4">
									<div className="w-full max-w-md rounded-lg border border-[#ff3131]/30 bg-[#ff3131]/10 p-4">
										<p className="text-sm font-semibold text-[#ff3131]">
											{t('pages.projectDetail.artifactPreview.errorLoading')}
										</p>
										<p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
									</div>
								</div>
							) : content !== null ? (
								markdown ? (
									<Streamdown
										className="prose dark:prose-invert prose-sm max-w-none p-4 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
										plugins={{ code, cjk }}
									>
										{content}
									</Streamdown>
								) : (
									<pre className="whitespace-pre-wrap break-words p-4 text-sm font-mono text-foreground">
										{content}
									</pre>
								)
							) : (
								<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
									<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
									<p className="mt-3 text-sm font-medium text-foreground">
										{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{t('pages.projectDetail.artifactPreview.previewNotAvailableDescription')}
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{activeTab === 'metadata' && (
				<div className="space-y-6 p-4">
					<ArtifactMetadataBlock artifact={artifact} />
				</div>
			)}
		</div>
	)
}
